import { useState } from 'react';

const STORAGE_KEY = 'bmb_invite_access';

const validPasswords: string[] = (import.meta.env.VITE_INVITE_PASSWORDS ?? '')
  .split(',')
  .map((p: string) => p.trim())
  .filter(Boolean);

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const MusicNote = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
  </svg>
);

export function SitePasswordGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    if (validPasswords.length === 0) return true;
    // Storage holds a SHA-256 hash of the password, not the raw value
    return localStorage.getItem(STORAGE_KEY) !== null;
  });
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  if (unlocked) return <>{children}</>;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validPasswords.includes(input)) {
      // Store a hash so the raw password is never recoverable from storage
      const hash = await sha256(input);
      localStorage.setItem(STORAGE_KEY, hash);
      setUnlocked(true);
    } else {
      setError(true);
      setInput('');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-radial from-primary-600/10 via-transparent to-transparent animate-pulse-slow" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-radial from-primary-800/8 via-transparent to-transparent animate-pulse-slower" />
      </div>

      {/* Floating music notes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <MusicNote className="absolute top-[15%] left-[10%] w-6 h-6 text-primary-600/20 animate-float-1" />
        <MusicNote className="absolute top-[25%] right-[15%] w-8 h-8 text-primary-500/15 animate-float-2" />
        <MusicNote className="absolute bottom-[20%] left-[20%] w-5 h-5 text-primary-700/20 animate-float-3" />
        <MusicNote className="absolute bottom-[30%] right-[10%] w-7 h-7 text-primary-600/15 animate-float-1" />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4 animate-fadeIn">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-linear-to-br from-primary-500 to-primary-700 shadow-lg shadow-primary-600/30 mb-6">
            <MusicNote className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-dark-text-primary tracking-tight mb-2">
            BookMyBand
          </h1>
          <p className="text-dark-text-secondary text-base">
            The booking app for busy musicians
          </p>
        </div>

        {/* Card */}
        <div className="bg-dark-surface/80 backdrop-blur-xl rounded-2xl border border-dark-border/50 shadow-2xl shadow-black/50 p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-dark-text-primary mb-2">
              Beta Access
            </h2>
            <p className="text-dark-text-secondary text-sm">
              Enter your invitation password to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              className="input w-full"
              placeholder="Invitation password"
              value={input}
              onChange={(e) => { setInput(e.target.value); setError(false); }}
              autoFocus
              autoComplete="off"
            />
            {error && (
              <p className="text-red-400 text-sm">
                Incorrect password. Please try again.
              </p>
            )}
            <button
              type="submit"
              className="w-full py-3 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold transition-colors duration-200 shadow-lg shadow-primary-600/30"
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
